import { create } from 'zustand';
import { supabase } from '../lib/supabase';
import type { User, Session } from '@supabase/supabase-js';
import type { Provider } from '@supabase/supabase-js';
import { Capacitor } from '@capacitor/core';
import { Browser } from '@capacitor/browser';

interface AuthState {
  user: User | null;
  session: Session | null;
  loading: boolean;
  isAuthenticated: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string) => Promise<void>;
  signInWithProvider: (provider: Provider, redirectTo?: string) => Promise<void>;
  signOut: () => Promise<void>;
  setUser: (user: User | null) => void;
  setSession: (session: Session | null) => void;
  setIsAuthenticated: (value: boolean) => void;
  refreshSession: () => Promise<void>;
}

export const useAuthStore = create<AuthState>((set) => ({
  user: null,
  session: null,
  loading: true,
  isAuthenticated: false,
  signIn: async (email: string, password: string) => {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    if (error) throw error;
    
    if (data.user && data.session) {
      set({ 
        user: data.user, 
        session: data.session,
        isAuthenticated: true 
      });
    }
  },
  signUp: async (email: string, password: string) => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
    });
    if (error) throw error;
    
    if (data.user && data.session) {
      set({ 
        user: data.user, 
        session: data.session,
        isAuthenticated: true 
      });
    }
  },
  signInWithProvider: async (provider: Provider, redirectTo?: string) => {
    const currentUrl = new URL(window.location.href);
    const invitationId = currentUrl.searchParams.get('invitation');
    const isNative = Capacitor.isNativePlatform();

    // On native, redirect back into the app via custom scheme so the OS routes
    // the callback to the native app rather than opening a browser tab.
    const baseRedirectUrl = isNative
      ? 'com.hustli.app://auth/callback'
      : (import.meta.env.VITE_AUTH_REDIRECT_URL || `${window.location.origin}/auth/callback`);

    let redirectUrl = baseRedirectUrl;
    if (invitationId) {
      redirectUrl += `?invitation=${invitationId}`;
    }
    if (redirectTo) {
      redirectUrl = redirectTo;
    }

    if (isNative) {
      // skipBrowserRedirect=true returns the OAuth URL without navigating the WebView.
      // We open it in Capacitor's in-app browser so the user stays in the native context.
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider,
        options: {
          redirectTo: redirectUrl,
          skipBrowserRedirect: true,
          queryParams: { access_type: 'offline', prompt: 'consent' },
        }
      });
      if (error) throw error;
      if (data?.url) {
        await Browser.open({ url: data.url });
      }
    } else {
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider,
        options: {
          redirectTo: redirectUrl,
          queryParams: { access_type: 'offline', prompt: 'consent' },
        }
      });
      if (error) throw error;
    }
  },
  signOut: async () => {
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
    set({ user: null, session: null, isAuthenticated: false });
  },
  setUser: (user) => set({ user }),
  setSession: (session) => set({ session }),
  setIsAuthenticated: (value) => set({ isAuthenticated: value }),
  refreshSession: async () => {
    try {
      const { data: { session }, error } = await supabase.auth.getSession();
      if (error) throw error;
      
      if (session) {
        set({ 
          user: session.user,
          session: session,
          isAuthenticated: true
        });
      } else {
        set({ 
          user: null,
          session: null,
          isAuthenticated: false
        });
      }
    } catch (error) {
      console.error('Error refreshing session:', error);
      set({ 
        user: null,
        session: null,
        isAuthenticated: false
      });
    }
  }
}));